// ─────────────────────────────────────────────────────────────────────────────
// P18-ter — botón "Descargar reporte global" de Reportería → Históricos. A diferencia de
// `CampaignRowActions` (una fila, una campaña), este exporta TODAS las campañas del alcance en un
// solo Excel, calcado de `03 - Historicos.xlsx` (hojas "Resumen Ejecutivo" + "Definiciones" +
// "Campañas Históricas").
//
// El backend (`GET campaigns-export-global`) ya resuelve el agregado deduplicado (Audiencia/
// Participantes/Staff/Frontline como UNIÓN de campañas, no suma ingenua — ver doc-comment de
// `CampaignsGlobalSummaryDto`) y una fila por campaña con el MISMO cálculo que `campaign-summary`/
// `campaign-export`. Este componente solo prepara los datos: zipea esas filas contra el catálogo de
// campañas que la pestaña Históricos le pasa por `campaigns` (Nombre/Descripción/Tipo/Estado/Alcance — NO viajan en
// el DTO del backend, mismo criterio que tuvo `CampaignRowActions`, eliminado el 2026-09-29) y los traduce al idioma activo.
//
// La forma visual de las tres hojas (paleta, banner, anchos, formatos numéricos y bordes, todos
// medidos de la plantilla del cliente) vive en `historicosExcel.ts`.
// ─────────────────────────────────────────────────────────────────────────────
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Toast, useToast } from '@/components/common';
import { recognitionReportsApi, getCampaignName, getRecognitionErrorMessage } from '@/api/recognition';
import type { CampaignDto } from '@/api/recognition';
import { type ExcelCellValue } from '@/utils/exportExcelMultiSheet';
import { exportarLibro } from '../exportarLibro';
import { useOuNameResolver } from '@/views/AnalyticsView/hooks/useAnalyticsNames';
import { buildHistoricosExcelSheets, type FilaCampanaHistorico } from './historicosExcel';
import { estadoVisible } from '../reportsShared';
import { campanaCerrada, diasDeCampana } from '../campanasReporte';

const SIN_DATO = '—';
const NOMBRE_ARCHIVO = 'reporteria-campanas-global';

/** `canExportData` default `true`: ver nota en `DailyTab.tsx`/`CampaignRowActions.tsx`. */
export function CampaignsGlobalExportButton(
  { campaigns, canExportData = true }: Readonly<{ campaigns: CampaignDto[]; canExportData?: boolean }>,
) {
  const { t, i18n } = useTranslation('recognition');
  const lang = i18n.language.split('-')[0];
  const { toast, showError } = useToast();
  const { resolveOuName } = useOuNameResolver();
  const [isDownloading, setIsDownloading] = useState(false);

  /**
   * "Duración de la campaña" en DÍAS (2026-10-06, pedido del cliente: antes eran meses redondos y una
   * campaña de pocos días salía como «0 meses»). Ver `diasDeCampana`.
   */
  function duracionTexto(inicio: string | null, fin: string | null): string {
    const dias = diasDeCampana(inicio, fin);
    return dias === null ? SIN_DATO : t('reports.history.excel.durationDays', { count: dias });
  }

  function formatFecha(fecha: string | null): string {
    return fecha ? new Date(fecha).toLocaleDateString(lang) : SIN_DATO;
  }

  async function handleDownload() {
    setIsDownloading(true);
    try {
      const dto = await recognitionReportsApi.getCampaignsGlobalExport();
      const filaPorCampana = new Map(dto.campanas.map((fila) => [fila.campaignId, fila]));

      // El guion de «sin dato» sustituye al 0 cuando el agregado está suprimido por anonimato: un
      // cero se leería como "nadie participó" en vez de "no se puede mostrar".
      const cifraGlobal = (valor: number): ExcelCellValue => (dto.suprimido ? SIN_DATO : valor);

      // 2026-10-06 (pedido del cliente) — la hoja «Campañas Históricas» lista solo campañas CERRADAS,
      // con el mismo criterio que la tabla de la pestaña (`campanaCerrada`). Decisión del usuario:
      // el Resumen Ejecutivo NO cambia y sigue contando todas las campañas que calcula el backend
      // (HIS-F4), así que sus totales pueden ser mayores que la suma de esta hoja.
      const campanas: FilaCampanaHistorico[] = campaigns.filter((c) => campanaCerrada(c)).map((campaign) => {
        const fila = filaPorCampana.get(campaign.id);
        const cifra = (valor: number | undefined): ExcelCellValue =>
          (fila?.suprimido ? SIN_DATO : (valor ?? SIN_DATO));

        return {
          nombre: getCampaignName(campaign, lang),
          tipo: t(`reports.campaigns.type.${campaign.tipo}`),
          audienciaOu: campaign.alcance.length === 0
            ? t('reports.common.scopeAll')
            : campaign.alcance.map(resolveOuName).join(', '),
          audiencia: fila?.audiencia ?? SIN_DATO,
          participantes: cifra(fila?.participantes),
          participantesStaff: cifra(fila?.participantesStaff),
          participantesFrontline: cifra(fila?.participantesFrontline),
          duracion: duracionTexto(campaign.fechaInicio, campaign.fechaFin),
          reconocimientos: cifra(fila?.reconocimientos),
          expiracion: formatFecha(campaign.fechaFin),
          estadoKey: estadoVisible(campaign.estado),
          estado: t(`reports.history.status.${estadoVisible(campaign.estado)}`),
          descripcion: campaign.descripcion?.[lang] ?? campaign.descripcion?.es ?? SIN_DATO,
        };
      });

      const sheets = buildHistoricosExcelSheets({
        traducir: (clave: string, opciones?: Record<string, unknown>) =>
          t(`reports.history.${clave}`, opciones ?? {}),
        kpis: {
          totalCampanas: dto.totalCampanas,
          totalReconocimientos: cifraGlobal(dto.totalReconocimientos),
          // La Audiencia NO se suprime: es el denominador configurado de la campaña, no un conteo
          // de personas identificables (mismo criterio que `CampaignSummaryDto.audiencia`).
          audienciaTotal: dto.audienciaTotal,
          participantes: cifraGlobal(dto.participantes),
          participantesStaff: cifraGlobal(dto.participantesStaff),
          participantesFrontline: cifraGlobal(dto.participantesFrontline),
          porcentajeParticipacion: cifraGlobal(dto.porcentajeParticipacion),
          promedioPorParticipante: cifraGlobal(dto.promedioPorParticipante),
        },
        campanas,
        filtro: t('reports.history.excel.filterAllCampaigns'),
        generado: new Date().toLocaleString(lang),
      });

      await exportarLibro({ fileName: NOMBRE_ARCHIVO, sheets });
    } catch (error: unknown) {
      showError(getRecognitionErrorMessage(error, t('reports.history.downloadGlobalError')));
      if (import.meta.env.DEV) console.error('No se pudo descargar el reporte global de campañas:', error);
    } finally {
      setIsDownloading(false);
    }
  }

  return (
    <>
      {canExportData && (
        <button type="button" className="rr-btn" onClick={handleDownload} disabled={isDownloading}>
          <i
            className={isDownloading ? 'fas fa-spinner fa-spin' : 'fas fa-file-excel'}
            aria-hidden="true"
          />{' '}
          {isDownloading ? t('reports.shine.exporting') : t('reports.history.downloadGlobalExcel')}
        </button>
      )}
      <Toast toast={toast} />
    </>
  );
}
